import { useState } from 'react';

import { BracketsCurly, MagnifyingGlass } from '@phosphor-icons/react';

import { Icon } from '@clay-run/glaze/icon';
import { InputField } from '@clay-run/glaze/input-field';
import { Popover, PopoverButton, PopoverPanel } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';

import {
  filterTokenFields,
  tokenRowValue,
  tokenTargetLabel,
  type TokenRowValue,
  type TokenTargetField,
} from '../token-insert';

/**
 * Playbooks send PRD, Unit U9 (R10 / Key Technical Decision 10) — the one
 * token-insertion control for every subject/body field in the app.
 *
 * ── ONE BUTTON PER STEP, NOT ONE PER FIELD (prod QA, 2026-09-16) ──────────────
 * One instance per step, left-aligned (`self-start`) under the Body field, and
 * told by {@link target} which field the rep was last in — see
 * `token-insert.ts#resolveTokenTarget` for why. Stateless: it calls
 * `onInsert(token)` and the host, which owns both fields and their caret
 * records, does the insert. It replaces the branch/library editor's flat chip
 * row and launcher's append-only `VariablePicker`.
 *
 * ── ROWS SHOW THE RESOLVED VALUE, NOT A SENTENCE ──────────────────────────────
 * Each row is `{{Token}}` on the left and what it resolves to on the right, in
 * a tone from {@link tokenRowValue} (decision 4). Round 3's version spelled out
 * full sentences ("Resolves to X" / "Empty for Y"), which read as too much for
 * what is essentially a lookup list. The three tones are load-bearing, not
 * decoration: `filled` (action) means this token has a value for the sample
 * member, `empty` (warning) means it is genuinely blank and the rep is about to
 * send a gap, and `neutral` (tertiary) means nothing has been evaluated — Clay
 * resolves it downstream, or a segmentation supplies the sender, or there is no
 * sample member at all. Only `empty` should pull the eye.
 *
 * Semantic tokens only (`text-content-action` / `text-icon-warning` /
 * `text-content-tertiary`), per CLAUDE.md's design-system rule and the plan's
 * "Deferred to Implementation" note that left exact spacing to the implementer.
 */
export function TokenPicker({
  onInsert,
  target,
  sampleVars,
  sender,
}: {
  /**
   * Called with the braced token text (`'{{First Name}}'`). The caller inserts
   * it into {@link target} at that field's recorded caret — see
   * `useCaretTracker#insert`.
   */
  onInsert: (token: string) => void;
  /**
   * Which field this button's insert will land in — the caller's
   * `resolveTokenTarget(tracker.lastField)`. Rendered as the panel's
   * "Insert into …" header; this component never decides it, so the one place
   * the last-field rule lives is `token-insert.ts` (unit-tested, DOM-free).
   */
  target: TokenTargetField;
  /**
   * The sample member's resolved token values, or `null` when there is nothing
   * to resolve against (library mode, or before a campaign is chosen). Computed
   * by the caller as
   * `sampleMember && campaign !== undefined ? buildTemplateVars(sampleMember, campaign, toSenderValue(sender)) : null`
   * so this component stays free of campaign/member types.
   */
  sampleVars: Record<string, string> | null;
  /** The sequence's effective sender, or `null` when a segmentation supplies it. */
  sender: string | null;
}) {
  /**
   * Plain `useState`, like every other local control state in this app's
   * popovers (see `SequenceSenderPopover`): playbooks mounts no
   * `artifact:updated` SSE subscriber, so the draft-state-under-SSE re-render
   * hazard does not apply to a transient search string. The CARET, which does
   * have to survive re-renders, lives in refs inside `useCaretTracker` instead.
   */
  const [query, setQuery] = useState('');
  const rows = filterTokenFields(query);

  return (
    <Popover>
      {/* `data-row-control` — Config's `RowShell` toggles expand/collapse on a
          header click and skips anything carrying this attribute (see
          `row-click-guard.ts#isInteractiveRowTarget`), so opening the picker
          must not also collapse the row the editor sits in. */}
      {/* `self-start` (prod QA, 2026-09-16) — Glaze's `PopoverButton` renders
          a real `<button>` and forwards `className` straight onto it
          (`popover.tsx` → `Button`), and both hosts put that button directly
          in a `flex flex-col`, whose default `align-items: stretch` is what
          rendered the trigger as a full-width bar with a centred "Tokens"
          label. This pins it to its natural width at the left edge, which is
          what makes it read as a small control belonging to the copy above it
          rather than as a section divider. */}
      <PopoverButton
        variant="ghost"
        size="xs"
        className="mt-1 self-start"
        aria-haspopup="listbox"
        data-row-control
      >
        {/* `{{ }}` — the picker inserts a merge token, so the brace icon says
            what the button does better than a generic settings glyph. */}
        <Icon type={BracketsCurly} size="xs" />
        Tokens
      </PopoverButton>
      <PopoverPanel positioner={{ side: 'bottom', align: 'start' }}>
        <div className="flex max-h-80 w-80 flex-col gap-1 p-2" data-row-control>
          {/* Prod QA, 2026-09-16 — with one button for two fields, the rep has
              to be able to see which one is about to receive the token BEFORE
              clicking a row. Deliberately a plain line of text and not a
              field PICKER: the target follows where the rep was already
              typing, so offering a second way to choose it would just be a
              control that is always already correct. */}
          <Text size="xs" variant="tertiary" className="px-2 pt-0.5">
            Insert into {tokenTargetLabel(target)}
          </Text>
          <InputField
            type="search"
            size="xs"
            leadingIcon={MagnifyingGlass}
            aria-label="Search tokens"
            placeholder="Search tokens"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="flex flex-col overflow-y-auto" role="listbox" aria-label="Tokens">
            {rows.map((field) => {
              const resolved = tokenRowValue(field, sampleVars, sender);
              return (
                <button
                  key={field.variable}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="flex items-center gap-2 rounded px-2 py-1 text-left hover:bg-bg-secondary-hover"
                  // The rep clicks this while the copy field still holds the
                  // caret we are about to insert at. A mousedown that moves
                  // focus would blow that away before the click ever fires —
                  // for the rich body it would also collapse the live
                  // selection the range was cloned from.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onInsert(field.variable)}
                >
                  <span className="font-mono text-xs text-content-primary">{field.variable}</span>
                  <span
                    className={`ml-auto shrink-0 truncate text-xs ${toneClass(resolved.tone)}`}
                    style={{ maxWidth: '9rem' }}
                  >
                    {resolved.value}
                  </span>
                </button>
              );
            })}
            {rows.length === 0 && (
              <Text size="xs" variant="tertiary" className="px-2 py-1">
                No tokens match “{query.trim()}”.
              </Text>
            )}
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}

/** Tone → semantic color token. Mirrors the prototype's three `toneCls` values. */
function toneClass(tone: TokenRowValue['tone']): string {
  if (tone === 'filled') return 'text-content-action';
  if (tone === 'empty') return 'text-icon-warning';
  return 'text-content-tertiary';
}
